"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useCart } from "@/store/hooks";
import {
  ORDER_TYPE,
  getProductDetailUrl,
  getProductOrderType,
} from "@/lib/orderType";
import "./modal.css";

function getImages(product, imageOverride) {
  const images = [];

  if (imageOverride) images.push(imageOverride);

  const variant = product?.variants?.[0];
  (variant?.images || []).forEach((image) => {
    const url = typeof image === "string" ? image : image?.url;
    if (url && !images.includes(url)) images.push(url);
  });

  if (product?.thumbnail && !images.includes(product.thumbnail)) {
    images.push(product.thumbnail);
  }

  (product?.images || []).forEach((image) => {
    const url = typeof image === "string" ? image : image?.url;
    if (url && !images.includes(url)) images.push(url);
  });

  return images.length ? images : ["/assets/images/product-placeholder.png"];
}

function getVariant(product) {
  return product?.variants?.[0] || null;
}

function getPrice(variant) {
  return (
    variant?.calculated_price?.calculated_amount_with_tax ??
    variant?.calculated_price?.calculated_amount ??
    variant?.calculated_price?.original_amount ??
    variant?.prices?.[0]?.amount ??
    null
  );
}

function getCurrency(variant) {
  return (
    variant?.calculated_price?.currency_code ||
    variant?.prices?.[0]?.currency_code ||
    "cad"
  );
}

function formatPrice(amount, currency = "cad") {
  if (amount === null || amount === undefined) return "Price unavailable";

  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount));
}

function getVariantSize(product, variant) {
  if (variant?.title && !/^default( variant)?$/i.test(variant.title)) {
    return variant.title;
  }

  return (
    product?.metadata?.pack_size ||
    product?.metadata?.size ||
    product?.metadata?.weight ||
    product?.subtitle ||
    ""
  );
}

export default function ProductQuickViewModal({
  product,
  imageOverride,
  open,
  onClose,
}) {
  const orderType = getProductOrderType(product);
  const { cart, addToCart, updateItemQuantity } = useCart(
    orderType || ORDER_TYPE.GROCERY,
  );

  const [activeImage, setActiveImage] = useState(0);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");

  const variant = getVariant(product);
  const images = useMemo(
    () => getImages(product, imageOverride),
    [product, imageOverride],
  );

  const price = getPrice(variant);
  const currency = getCurrency(variant);
  const size = getVariantSize(product, variant);

  const cartLine = (cart?.items || []).find(
    (item) =>
      item.variant_id === variant?.id || item.variant?.id === variant?.id,
  );

  const quantity = cartLine?.quantity || 0;

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleEscape(event) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setActiveImage(0);
      setMessage("");
    }
  }, [open, product?.id]);

  if (!open || !product) return null;

  async function increaseQuantity() {
    if (!variant?.id || adding) return;

    if (!orderType) {
      setMessage("Product Type must be Grocery or Prepared Food in Medusa.");
      return;
    }

    setAdding(true);
    setMessage("");

    try {
      if (cartLine) {
        await updateItemQuantity(cartLine.id, quantity + 1);
      } else {
        await addToCart(variant.id, 1);
      }
    } catch (error) {
      setMessage(error?.message || "Unable to add item.");
    } finally {
      setAdding(false);
    }
  }

  async function decreaseQuantity() {
    if (!cartLine || adding) return;

    setAdding(true);
    setMessage("");

    try {
      await updateItemQuantity(cartLine.id, Math.max(quantity - 1, 0));
    } catch (error) {
      setMessage(error?.message || "Unable to update item.");
    } finally {
      setAdding(false);
    }
  }

  const productUrl = getProductDetailUrl(product, variant?.id);

  return (
    <div
      className="asian-bucket-product-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="asian-bucket-product-modal-main-wrapper"
        role="dialog"
        aria-modal="true"
        aria-label={product.title}
      >
        <button
          type="button"
          className="asian-bucket-product-modal-close-button"
          onClick={onClose}
          aria-label="Close product"
        >
          ×
        </button>

        <div className="asian-bucket-product-modal-content-main-wrapper">
          <div className="asian-bucket-product-modal-gallery-main-wrapper">
            <div className="asian-bucket-product-modal-thumbnails">
              {images.slice(0, 4).map((image, index) => (
                <button
                  type="button"
                  key={`${image}-${index}`}
                  className={
                    activeImage === index
                      ? "asian-bucket-product-modal-thumbnail active"
                      : "asian-bucket-product-modal-thumbnail"
                  }
                  onClick={() => setActiveImage(index)}
                >
                  <img src={image} alt={`${product.title} ${index + 1}`} />
                </button>
              ))}
            </div>

            <div className="asian-bucket-product-modal-main-image">
              <img src={images[activeImage]} alt={product.title} />
            </div>
          </div>

          <div className="asian-bucket-product-modal-info-main-wrapper">
            <span className="asian-bucket-product-modal-brand-badge">
              {product?.metadata?.badge ||
                product?.metadata?.brand ||
                "Trusted Brand"}
            </span>

            <h2>{product.title}</h2>
            {size && <p className="asian-bucket-product-modal-size">{size}</p>}

            <div className="asian-bucket-product-modal-price">
              {formatPrice(price, currency)}
            </div>

            {product?.metadata?.unit_price && (
              <p className="asian-bucket-product-modal-unit-price">
                {product.metadata.unit_price}
              </p>
            )}

            {Number(product?.metadata?.review_count || 0) > 0 && (
              <div className="asian-bucket-product-modal-rating">
                <span>★★★★★</span>
                <b>{Number(product?.metadata?.rating || 0).toFixed(1)}</b>
                <small>
                  ({Number(product?.metadata?.review_count || 0).toLocaleString()} reviews)
                </small>
              </div>
            )}

            <p className="asian-bucket-product-modal-description">
              {product.description ||
                "Authentic quality product selected for Asian Basket customers."}
            </p>

            <div className="asian-bucket-product-modal-cart-row">
              {quantity === 0 ? (
                <button
                  type="button"
                  className="asian-bucket-product-modal-single-add-button"
                  onClick={increaseQuantity}
                  disabled={adding}
                  aria-label={`Add ${product.title} to basket`}
                >
                  {adding ? "..." : "+"}
                </button>
              ) : (
                <div className="asian-bucket-product-modal-quantity">
                  <button
                    type="button"
                    onClick={decreaseQuantity}
                    disabled={adding}
                    aria-label={`Remove one ${product.title}`}
                  >
                    −
                  </button>
                  <span>{quantity}</span>
                  <button
                    type="button"
                    onClick={increaseQuantity}
                    disabled={adding}
                    aria-label={`Add another ${product.title}`}
                  >
                    +
                  </button>
                </div>
              )}
            </div>

            {message && (
              <p className="asian-bucket-product-modal-message">{message}</p>
            )}
          </div>
        </div>

        <div className="asian-bucket-product-modal-features-main-wrapper">
          {[1, 2, 3, 4].map((number) => {
            const value = product?.metadata?.[`feature_${number}`];
            if (!value) return null;
            const icon = number === 2 ? "shield.svg" : number === 4 ? "basket.svg" : "leaf.svg";

            return (
              <div key={number}>
                <img src={`/assets/icons/${icon}`} alt="" />
                <span><b>{value}</b></span>
              </div>
            );
          })}
        </div>

        <div className="asian-bucket-product-modal-footer">
          <Link href={productUrl} onClick={onClose}>
            View Full Product Details
            <img src="/assets/icons/arrow-right.svg" alt="" />
          </Link>
        </div>
      </div>
    </div>
  );
}
